import { tipoVisita, lugarVisita } from '../lib/formato';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListaEventos } from '../components/ListaEventos';
import { CabeceraPagina } from '../components/Armazon';
import { Aviso, Boton, Cargando, Tarjeta, Vacio } from '../components/ui';
import { api, qs } from '../lib/api';
import {
  NOMBRE_MES,
  TIPO_EVENTO_ETIQUETA,
  fechaLarga,
  finDia,
  hora,
  inicioDia,
  inicioMes,
  inicioSemana,
  mismoDia,
  sumaDias,
} from '../lib/formato';
import { useDatos, useSesion } from '../lib/sesion';
import type { EventoAgenda } from '../lib/tipos';

type Vista = 'lista' | 'mes' | 'semana' | 'dia';

const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const HORAS = Array.from({ length: 15 }, (_, i) => i + 7); // 07:00 a 21:00

export function Agenda() {
  const navegar = useNavigate();
  const { es } = useSesion();
  const [params, setParams] = useSearchParams();
  const vista: Vista = (['lista', 'semana', 'dia', 'mes'].includes(params.get('vista') ?? '')
    ? params.get('vista') : window.innerWidth < 768 ? 'lista' : 'semana') as Vista;
  const setVista = (v: Vista) => setParams(p => { const n = new URLSearchParams(p); n.set('vista', v); return n; });
  const [ancla, setAncla] = useState(() => new Date());

  const { desde, hasta } = useMemo(() => {
    if (vista === 'dia') return { desde: inicioDia(ancla), hasta: finDia(ancla) };
    if (vista === 'semana') {
      const l = inicioSemana(ancla);
      return { desde: l, hasta: finDia(sumaDias(l, 6)) };
    }
    const primero = inicioMes(ancla);
    const inicio = inicioSemana(primero);
    return { desde: inicio, hasta: finDia(sumaDias(inicio, 41)) };
  }, [vista, ancla]);

  const { datos, cargando, error } = useDatos(
    (s) =>
      vista === 'lista' ? Promise.resolve({ events: [] as EventoAgenda[] }) : api.get<{ events: EventoAgenda[] }>(
        `/calendar/events${qs({ from: desde.toISOString(), to: hasta.toISOString() })}`,
        s,
      ),
    [vista, desde.getTime(), hasta.getTime()],
  );

  const eventos = datos?.events ?? [];

  const paso = (n: number) =>
    setAncla((d) =>
      vista === 'mes'
        ? new Date(d.getFullYear(), d.getMonth() + n, 1)
        : sumaDias(d, n * (vista === 'semana' ? 7 : 1)),
    );

  const titulo =
    vista === 'dia'
      ? fechaLarga(ancla)
      : vista === 'semana'
        ? `Semana del ${inicioSemana(ancla).getDate()} al ${sumaDias(inicioSemana(ancla), 6).getDate()} de ${NOMBRE_MES.format(ancla)}`
        : NOMBRE_MES.format(ancla);

  return (
    <>
      <CabeceraPagina
        titulo="Agenda"
        acciones={<><Boton variante="secundario" onClick={() => setVista('lista')}>Ver todos los eventos</Boton>
          {es('SALESPERSON', 'ADMIN') && <Boton variante="clave" onClick={() => navegar('/solicitudes/nueva')}>Nueva solicitud</Boton>}</>}

      />

      <div className="agenda-barra">
        <div className="selector-vista">
          {(['lista', 'semana', 'mes', 'dia'] as Vista[]).map((v) => (
            <button key={v} aria-pressed={vista === v} onClick={() => setVista(v)}>
              {v === 'lista' ? 'Lista' : v === 'mes' ? 'Mes' : v === 'semana' ? 'Semana' : 'Día'}
            </button>
          ))}
        </div>
        {vista !== 'lista' && <><Boton variante="secundario" tam="s" onClick={() => paso(-1)} aria-label="Periodo anterior">
          ←
        </Boton>
        <Boton variante="secundario" tam="s" onClick={() => setAncla(new Date())}>
          Hoy
        </Boton>
        <Boton variante="secundario" tam="s" onClick={() => paso(1)} aria-label="Periodo siguiente">
          →
        </Boton>
        <strong style={{ marginLeft: 8, textTransform: 'capitalize' }}>{titulo}</strong></>}
      </div>

      {error && <Aviso tono="error">{error}</Aviso>}

      {cargando ? (
        <Cargando texto="Cargando la agenda…" />
      ) : vista === 'lista' ? (
        <ListaEventos />
      ) : vista === 'mes' ? (
        <VistaMes desde={desde} ancla={ancla} eventos={eventos} onAbrir={navegar} />
      ) : (
        <VistaHoras
          dias={
            vista === 'semana'
              ? Array.from({ length: 7 }, (_, i) => sumaDias(inicioSemana(ancla), i))
              : [ancla]
          }
          eventos={eventos}
          onAbrir={navegar}
        />
      )}

      {vista !== 'lista' && <div className="leyenda">
        <span>
          <i style={{ background: 'var(--oliva-suave)', border: '1px solid var(--oliva)' }} />
          {TIPO_EVENTO_ETIQUETA.OWN_VISIT}
        </span>
        <span>
          <i style={{ background: 'var(--azafran-suave)', border: '1px dashed var(--azafran)' }} />
          {TIPO_EVENTO_ETIQUETA.PROPOSED}
        </span>
        <span>
          <i style={{ background: 'var(--granate-suave)', border: '1px solid var(--granate)' }} />
          {TIPO_EVENTO_ETIQUETA.UNAVAILABLE}
        </span>
        <span>
          <i style={{ background: 'var(--acero-suave)', border: '1px solid var(--acero-claro)' }} />
          {TIPO_EVENTO_ETIQUETA.BLOCKED}
        </span>
      </div>}
    </>
  );
}

function eventosDe(eventos: EventoAgenda[], dia: Date) {
  return eventos
    .filter((e) => {
      const i = new Date(e.start);
      const f = new Date(e.end);
      return i <= finDia(dia) && f >= inicioDia(dia);
    })
    .sort((a, b) => a.start.localeCompare(b.start));
}

function EventoBoton({
  e,
  onAbrir,
  compacto,
}: {
  e: EventoAgenda;
  onAbrir: (ruta: string) => void;
  compacto?: boolean;
}) {
  const clicable = Boolean(e.requestId) && e.type !== 'BLOCKED';
  return (
    <button
      className="evento"
      data-tipo={e.type}
      onClick={() => clicable && onAbrir(`/solicitudes/${e.requestId}`)}
      disabled={!clicable}
      title={`${TIPO_EVENTO_ETIQUETA[e.type]} · ${e.title}`}
    >
      <span className="cuando">
        {hora(e.start)}–{hora(e.end)}
      </span>
      {!compacto && <span>{e.title}</span>}
      {compacto && <span style={{ fontSize: 12 }}>{e.title}</span>}
      {e.visitType && <span>{tipoVisita(e.visitType)} · {lugarVisita(e.venueType, e.venueOther)}</span>}
    </button>
  );
}

function VistaMes({
  desde,
  ancla,
  eventos,
  onAbrir,
}: {
  desde: Date;
  ancla: Date;
  eventos: EventoAgenda[];
  onAbrir: (ruta: string) => void;
}) {
  const hoy = new Date();
  const dias = Array.from({ length: 42 }, (_, i) => sumaDias(desde, i));

  return (
    <div className="mes">
      {DOW.map((d) => (
        <div key={d} className="dow">
          {d}
        </div>
      ))}
      {dias.map((d) => {
        const delMes = d.getMonth() === ancla.getMonth();
        const lista = eventosDe(eventos, d);
        return (
          <div
            key={d.toISOString()}
            className="dia"
            data-fuera={!delMes ? 'true' : undefined}
            data-hoy={mismoDia(d, hoy) ? 'true' : undefined}
            data-vacio={lista.length === 0 ? 'true' : undefined}
          >
            <span className="num">{d.getDate()}</span>
            {lista.slice(0, 3).map((e, i) => (
              <EventoBoton key={i} e={e} onAbrir={onAbrir} compacto />
            ))}
            {lista.length > 3 && <span className="sutil">+{lista.length - 3} más</span>}
          </div>
        );
      })}
    </div>
  );
}

function VistaHoras({
  dias,
  eventos,
  onAbrir,
}: {
  dias: Date[];
  eventos: EventoAgenda[];
  onAbrir: (ruta: string) => void;
}) {
  return (
    <div className="pila">
      {dias.map((d) => {
        const lista = eventosDe(eventos, d);
        return (
          <Tarjeta
            key={d.toISOString()}
            titulo={<span style={{ textTransform: 'capitalize' }}>{fechaLarga(d)}</span>}
            extra={<span className="sutil">{lista.length} evento(s)</span>}
            sinRelleno
          >
            {lista.length === 0 ? (
              <Vacio titulo="Día libre" />
            ) : (
              <div className="horas">
                {HORAS.map((h) => {
                  const enHora = lista.filter((e) => {
                    const ini = new Date(e.start);
                    const fin = new Date(e.end);
                    const desdeH = new Date(d);
                    desdeH.setHours(h, 0, 0, 0);
                    const hastaH = new Date(d);
                    hastaH.setHours(h + 1, 0, 0, 0);
                    return ini < hastaH && fin > desdeH;
                  });
                  return (
                    <div key={h} style={{ display: 'contents' }}>
                      <div className="hora-etiqueta">{String(h).padStart(2, '0')}:00</div>
                      <div className="hora-celda">
                        {enHora
                          .filter((e) => new Date(e.start).getHours() === h || h === HORAS[0])
                          .map((e, i) => (
                            <EventoBoton key={i} e={e} onAbrir={onAbrir} />
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Tarjeta>
        );
      })}
    </div>
  );
}
